import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../App";
import { X } from "lucide-react";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AuthModal() {
  const { login, register, showAuthModal, setShowAuthModal } = useAuth();
  const { t } = useLanguage();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [resetToken, setResetToken] = useState("");

  if (!showAuthModal) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSubmitting(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else if (mode === "register") {
        await register(name, email, password);
      } else if (mode === "forgot") {
        const res = await axios.post(`/api/auth/forgot-password`, { email });
        if (res.data.token) {
          setResetToken(res.data.token);
          setMode("reset");
        } else {
          setSuccess(t("auth_forgot_title"));
        }
      } else if (mode === "reset") {
        await axios.post(`/api/auth/reset-password`, { token: resetToken, new_password: newPassword });
        setMode("login");
        setResetToken("");
        setNewPassword("");
        setSuccess(t("auth_reset_success"));
        setPassword("");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const switchMode = () => {
    setMode(mode === "login" ? "register" : "login");
    setError("");
    setSuccess("");
  };

  const close = () => {
    setShowAuthModal(false);
    setError("");
    setSuccess("");
    setEmail("");
    setPassword("");
    setName("");
    setNewPassword("");
    setResetToken("");
    setMode("login");
  };

  return (
    <div
      data-testid="auth-modal-overlay"
      onClick={close}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(8, 4, 2, 0.85)",
        backdropFilter: "blur(12px)",
      }}
    >
      <div
        data-testid="auth-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "420px",
          margin: "0 1rem",
          background: "#1A1008",
          border: "1px solid rgba(201,168,76,0.2)",
          borderRadius: "2px",
          padding: "2.5rem 2rem",
          position: "relative",
        }}
      >
        <button
          data-testid="auth-modal-close"
          onClick={close}
          style={{
            position: "absolute",
            top: "1rem",
            right: "1rem",
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "rgba(184,149,106,0.5)",
            padding: "0.25rem",
          }}
        >
          <X size={18} />
        </button>

        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <span
            className="font-logo"
            style={{
              fontSize: "1.6rem",
              letterSpacing: "0.3em",
              color: "#D4B878",
              textTransform: "uppercase",
              display: "block",
              marginBottom: "0.75rem",
            }}
          >
            KHATIRA
          </span>
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "0.88rem",
              color: "#B8956A",
              fontWeight: 300,
            }}
          >
            {mode === "login" ? t("auth_welcome") : mode === "register" ? t("auth_create") : mode === "forgot" ? t("auth_forgot_title") : t("auth_reset_title")}
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {mode === "register" && (
            <div>
              <label style={labelStyle}>{t("auth_name")}</label>
              <input
                data-testid="auth-name-input"
                className="khatira-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
          )}
          {(mode === "login" || mode === "register" || mode === "forgot") && (
            <div>
              <label style={labelStyle}>{t("auth_email")}</label>
              <input
                data-testid="auth-email-input"
                className="khatira-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          )}
          {(mode === "login" || mode === "register") && (
            <div>
              <label style={labelStyle}>{t("auth_password")}</label>
              <input
                data-testid="auth-password-input"
                className="khatira-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
          )}
          {mode === "reset" && (
            <div>
              <label style={labelStyle}>{t("auth_new_password")}</label>
              <input
                data-testid="auth-new-password-input"
                className="khatira-input"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
          )}

          {error && (
            <div
              data-testid="auth-error"
              style={{
                padding: "0.75rem 1rem",
                background: "rgba(180,60,40,0.08)",
                border: "1px solid rgba(180,60,40,0.25)",
                borderRadius: "2px",
                color: "#D4836A",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.82rem",
              }}
            >
              {error}
            </div>
          )}

          {success && (
            <div
              data-testid="auth-success"
              style={{
                padding: "0.75rem 1rem",
                background: "rgba(201,168,76,0.08)",
                border: "1px solid rgba(201,168,76,0.25)",
                borderRadius: "2px",
                color: "#D4B878",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.82rem",
              }}
            >
              {success}
            </div>
          )}

          <button
            data-testid="auth-submit-btn"
            className="btn-primary"
            type="submit"
            disabled={submitting}
            style={{
              padding: "0.85rem",
              fontSize: "0.85rem",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              marginTop: "0.5rem",
              width: "100%",
            }}
          >
            {submitting
              ? "..."
              : mode === "login"
                ? t("auth_login_btn")
                : mode === "register"
                  ? t("auth_register_btn")
                  : mode === "forgot"
                    ? t("auth_send_reset")
                    : t("auth_reset_btn")}
          </button>
        </form>

        <div style={{ textAlign: "center", marginTop: "1.5rem", display: "flex", flexDirection: "column", gap: "0.6rem", alignItems: "center" }}>
          {mode === "login" && (
            <button
              data-testid="auth-forgot-password-link"
              onClick={() => { setMode("forgot"); setError(""); setSuccess(""); }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.78rem",
                color: "rgba(184,149,106,0.6)",
                fontWeight: 300,
                letterSpacing: "0.02em",
              }}
            >
              {t("auth_forgot_link")}
            </button>
          )}
          {(mode === "forgot" || mode === "reset") && (
            <button
              data-testid="auth-back-to-login"
              onClick={() => { setMode("login"); setError(""); setSuccess(""); setNewPassword(""); }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.78rem",
                color: "rgba(184,149,106,0.6)",
                fontWeight: 300,
                letterSpacing: "0.02em",
              }}
            >
              {t("auth_back_to_login")}
            </button>
          )}
          <button
            data-testid="auth-switch-mode"
            onClick={switchMode}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "0.82rem",
              color: "#C9A84C",
              fontWeight: 400,
              letterSpacing: "0.02em",
            }}
          >
            {mode === "login" ? t("auth_switch_to_register") : t("auth_switch_to_login")}
          </button>
        </div>
      </div>
    </div>
  );
}

const labelStyle = {
  fontFamily: "'DM Sans', sans-serif",
  fontSize: "0.75rem",
  fontWeight: 400,
  color: "#B8956A",
  letterSpacing: "0.1em",
  display: "block",
  marginBottom: "0.4rem",
  textTransform: "uppercase",
};
